// ─────────────────────────────────────────────────────────────────────────────
// InviteModal — the multi-step "Invite User" wizard. This shell owns all state
// (step, form, submit status) and navigation/validation; each step under
// ./steps/* is a pure body renderer. The stepper + header + footer
// are chrome shared across steps 1–3; step 4 is the success screen.
//
//   1 User Details → 2 Role & Access → 3 Review & Send → ✓ Sent
//
// The real invite call (usersService.inviteUser) fires when leaving step 3.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./InviteModal.module.css";
import { inviteUser } from "../../../../services/usersService";
import Stepper from "./Stepper";
import UserDetailsStep from "./steps/UserDetailsStep";
import RoleAccessStep from "./steps/RoleAccessStep";
import ReviewStep from "./steps/ReviewStep";
import SuccessStep from "./steps/SuccessStep";
import { UserPlusIcon, CloseIcon, ChevronLeftIcon, ChevronRightIcon, MailIcon } from "./icons";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_FORM = {
  firstName: "", lastName: "", email: "",
  team: "", role: "",
  sendWelcomeEmail: true, requireMfaSetup: true,
};

export default function InviteModal({ onClose, onSuccess }) {
  const [step, setStep]       = useState(1);   // 1–3 form steps, 4 = success
  const [form, setForm]       = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Close on Escape (only while not mid-submit).
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !loading && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, loading]);

  // Whether the current step's inputs are complete enough to advance.
  const canAdvance = useMemo(() => {
    if (step === 1) {
      return EMAIL_RE.test(form.email.trim()) && form.firstName.trim() && form.lastName.trim();
    }
    if (step === 2) return Boolean(form.team && form.role);
    return true;
  }, [step, form]);

  const submit = async () => {
    setLoading(true); setError(null);
    try {
      await inviteUser({
        email: form.email.trim(),
        firstName: form.firstName.trim() || undefined,
        lastName: form.lastName.trim() || undefined,
        role: form.role,
        team: form.team || undefined,
        sendWelcomeEmail: form.sendWelcomeEmail,
        requireMfaSetup: form.requireMfaSetup,
      });
      onSuccess?.();
      setStep(4);
    } catch (err) {
      setError(err.message || "Failed to send invitation.");
    } finally {
      setLoading(false);
    }
  };

  const next = () => (step === 3 ? submit() : setStep((s) => s + 1));
  const back = () => setStep((s) => s - 1);
  const reset = () => { setForm(INITIAL_FORM); setError(null); setStep(1); };

  const isSuccess = step === 4;

  return createPortal(
    <div className={styles.overlay} onClick={() => !loading && onClose()}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <header className={styles.header}>
          <span className={styles.headerIcon}><UserPlusIcon /></span>
          <div className={styles.headerText}>
            <span className={styles.headerTitle}>Invite User</span>
            <span className={styles.headerSub}>Provision new users to your organization</span>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </header>

        {!isSuccess && <Stepper current={step} />}

        {/* ── Body ── */}
        <div className={styles.body}>
          {step === 1 && <UserDetailsStep form={form} update={update} />}
          {step === 2 && <RoleAccessStep form={form} update={update} />}
          {step === 3 && <ReviewStep form={form} />}
          {step === 4 && <SuccessStep form={form} />}

          {error && <div className={styles.error}>{error}</div>}
        </div>

        {/* ── Footer ── */}
        <footer className={styles.footer}>
          {isSuccess ? (
            <>
              <button className={styles.btnGhost} onClick={reset}>Invite Another</button>
              <button className={styles.btnPrimary} onClick={onClose}>Done</button>
            </>
          ) : (
            <>
              <button
                className={styles.btnGhost}
                onClick={step === 1 ? onClose : back}
                disabled={loading}
              >
                {step === 1 ? "Cancel" : <><ChevronLeftIcon /> Back</>}
              </button>
              <button
                className={styles.btnPrimary}
                onClick={next}
                disabled={!canAdvance || loading}
              >
                {step === 3
                  ? (loading ? "Sending…" : <><MailIcon size={15} /> Send Invite</>)
                  : <>Continue <ChevronRightIcon /></>}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
}
